// The signed-in frame: top bar (brand, workspace switcher, the sections Board · Fleet, page-specific
// controls, theme, the GitHub account link (for merging), settings, sign-out) and, on a phone, a
// bottom bar (Board, Fleet, Search, Settings). Fleet is for the workspace's owners only.
import { useState, type ReactNode } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { MARK_RING, MARK_STROKES, MARK_HULL_PATH } from '../lib/mark';
import { signOut } from '../api/client';
import { boardPath, fleetPath, settingsPath } from '../lib/routes';
import { findWorkspace, useSession } from '../lib/session';
import { NEXT_THEME, applyTheme, readTheme, type ThemePref } from '../lib/theme';
import { Icon } from './Icon';
import { GitHubAccount } from './Merge';
import { buttonClass, cn, Select, selectBox } from './ui';

/** The anchor cradling a hull, shared with the PWA icons (web/pwa.ts). */
export function Mark({ className }: { className?: string }) {
  return (
    <svg className={cn('size-[22px] flex-none', className)} viewBox="0 0 64 64" aria-hidden="true">
      <rect width="64" height="64" rx="15" className="fill-accent" />
      <circle cx={MARK_RING.cx} cy={MARK_RING.cy} r={MARK_RING.radius}
        className="fill-none stroke-accent-ink" strokeWidth={MARK_RING.width} />
      {MARK_STROKES.map(({ d, width }) => (
        <path key={d} d={d} className="fill-none stroke-accent-ink" strokeWidth={width} strokeLinecap="round" />
      ))}
      <path d={MARK_HULL_PATH} className="fill-accent-ink" />
    </svg>
  );
}

export function Brand() {
  return (
    <Link
      to="/"
      className="flex items-center gap-2 text-[14px] font-semibold tracking-[-0.01em] text-ink-display hover:no-underline"
      aria-label="Shipyard home"
    >
      <Mark />
      <span className="phone:hidden">Shipyard</span>
    </Link>
  );
}

/** The signed-in and signed-out frames' top bar. */
export const TOPBAR =
  'sticky top-0 z-20 flex flex-wrap items-center gap-2.5 border-b border-hairline bg-page px-5 py-2.5 phone:gap-2 phone:px-3 phone:py-2';
export const FRAME = 'flex min-h-screen flex-col';
export const MAIN = 'flex min-w-0 flex-1 flex-col';

const WS_DOT = <span className="size-2 flex-none rounded-[2px] bg-accent" aria-hidden="true" />;
const P_ITEM =
  'grid min-h-11 cursor-pointer justify-items-center gap-[3px] rounded border-0 bg-transparent py-1.5 text-[10.5px] no-underline hover:no-underline';

const THEME_LABEL: Record<ThemePref, string> = { system: 'System', light: 'Light', dark: 'Dark' };
const THEME_ICON = { system: 'system', light: 'sun', dark: 'moon' } as const;

export function ThemeToggle() {
  const [pref, setPref] = useState<ThemePref>(readTheme);
  const next = NEXT_THEME[pref];
  return (
    <button
      type="button"
      className={buttonClass('secondary', 'icon')}
      aria-label={`Theme: ${THEME_LABEL[pref]}. Switch to ${THEME_LABEL[next]}`}
      title={`Theme: ${THEME_LABEL[pref]}`}
      data-theme-pref={pref}
      onClick={() => {
        applyTheme(next);
        setPref(next);
      }}
    >
      <Icon name={THEME_ICON[pref]} />
    </button>
  );
}

function WorkspaceSwitcher({ ws }: { ws: number }) {
  const { me } = useSession();
  const navigate = useNavigate();
  const current = me.workspaces.find((w) => w.id === ws);
  if (me.workspaces.length < 2) {
    return (
      <span className={selectBox} aria-label={`Workspace: ${current?.name ?? ''}`}>
        {WS_DOT}
        {current?.name}
      </span>
    );
  }
  return (
    <Select
      lead={
        <>
          {WS_DOT}
          <span className="sr-only">Workspace</span>
        </>
      }
      value={ws}
      onChange={(e) => navigate(boardPath(Number(e.target.value)))}
      aria-label="Workspace"
    >
      {me.workspaces.map((w) => (
        <option key={w.id} value={w.id}>
          {w.name}
        </option>
      ))}
    </Select>
  );
}

function pItem({ isActive }: { isActive: boolean }): string {
  return cn(P_ITEM, isActive ? 'font-semibold text-accent-display' : 'text-ink-muted');
}

function topItem({ isActive }: { isActive: boolean }): string {
  return cn(
    'rounded px-2.5 py-1 text-[12.5px] font-medium no-underline hover:bg-surface hover:no-underline',
    isActive ? 'text-ink-display' : 'text-ink-muted hover:text-ink-display',
  );
}

/** Board · Fleet in the top bar (Fleet for owners only); the phone has its bottom bar instead. */
function SectionNav({ ws, owner }: { ws: number; owner: boolean }) {
  if (!owner) return null;
  return (
    <nav className="flex items-center gap-0.5 phone:hidden" aria-label="Pages">
      <NavLink to={boardPath(ws)} className={topItem}>
        Board
      </NavLink>
      <NavLink to={fleetPath(ws)} className={topItem}>
        Fleet
      </NavLink>
    </nav>
  );
}

export function Shell({
  ws,
  controls,
  children,
  onSearch,
}: {
  ws: number;
  controls?: ReactNode;
  children: ReactNode;
  onSearch?: () => void;
}) {
  const { me, reload } = useSession();
  const navigate = useNavigate();
  const who = me.user.name ?? me.user.email ?? me.user.identity;
  const owner = findWorkspace(me, ws)?.role === 'owner';
  return (
    <div className={FRAME}>
      <header className={TOPBAR}>
        <Brand />
        <span className="h-5 w-px bg-hairline phone:hidden" aria-hidden="true" />
        <WorkspaceSwitcher ws={ws} />
        <SectionNav ws={ws} owner={owner} />
        {controls}
        <span className="flex-1" />
        <ThemeToggle />
        <GitHubAccount github={me.github} onChange={reload} />
        <Link
          to={settingsPath(ws)}
          className={buttonClass('secondary', 'icon', 'hide-phone phone:hidden')}
          aria-label="Workspace settings"
          title="Workspace settings"
        >
          <Icon name="gear" />
        </Link>
        <button
          type="button"
          className={buttonClass('secondary', 'icon')}
          aria-label={`Sign out (${who})`}
          title={`Signed in as ${who}. Sign out`}
          onClick={async () => {
            await signOut();
            window.location.assign('/');
          }}
        >
          <Icon name="signout" />
        </button>
      </header>
      <main className={MAIN}>{children}</main>
      <nav
        className={cn(
          'fixed inset-x-0 bottom-0 z-[25] hidden border-t border-hairline bg-page px-2 pb-[calc(8px+env(safe-area-inset-bottom))] pt-1.5 phone:grid',
          owner ? 'grid-cols-4' : 'grid-cols-3',
        )}
        aria-label="Sections"
      >
        <NavLink to={boardPath(ws)} end className={pItem}>
          <Icon name="board" px={18} />
          Board
        </NavLink>
        {owner && (
          <NavLink to={fleetPath(ws)} className={pItem}>
            <Icon name="server" px={18} />
            Fleet
          </NavLink>
        )}
        <button
          type="button"
          className={pItem({ isActive: false })}
          onClick={() => {
            if (onSearch) onSearch();
            else navigate(boardPath(ws, 'search=1'));
          }}
        >
          <Icon name="search" px={18} />
          Search
        </button>
        <NavLink to={settingsPath(ws)} className={pItem}>
          <Icon name="gear" px={18} />
          Settings
        </NavLink>
      </nav>
    </div>
  );
}
